import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";
import { StepStatus } from "ui/Progress/Progress";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useAppSelector } from "fond/utils/hooks";

// The workflow has many steps, but we only show 3 in the UI.
const combinedWorkflowSteps = {
  inputs: [0, 1, 2, 3],
  counting: [4],
  calculate: [5],
};

interface GeneratePanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const GeneratePanel: React.FC<GeneratePanelProps> = ({ isReadOnly }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { workflowState, workflowStepIndex, runWorkflow } = useCustomWorkflow({ versionId, key: "nbn_cost_to_serve" });
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleGenerateCTS = async () => {
    runWorkflow?.();
  };

  /**
   * Determines the status of a step based on the workflow step index and state.
   */
  const getStatus = (steps: number[]): StepStatus => {
    if (workflowStepIndex == null) return "Idle";
    if (Math.max(...steps) < workflowStepIndex) return "Success";
    if (steps.includes(workflowStepIndex) && workflowState === "ERROR") return "Error";
    if (steps.includes(workflowStepIndex)) return "InProgress";
    if (workflowState !== "ERROR") return "Idle";

    return "NotReady";
  };

  return (
    <>
      {workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Cost to serve is ready to generate.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Cost to serve generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
        </Alert>
      )}

      {(isGeneratingCTS || workflowState === "ERROR") && (
        <Progress
          steps={[
            { ID: "inputs", Label: "Preparing inputs", Status: getStatus(combinedWorkflowSteps.inputs) },
            {
              ID: "counting",
              Label: "Counting materials and costs",
              Status: getStatus(combinedWorkflowSteps.counting),
            },
            {
              ID: "calculate",
              Label: "Calculating cost to serve",
              Status: getStatus(combinedWorkflowSteps.calculate),
            },
          ]}
        />
      )}

      {workflowState !== "COMPLETE" && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || isGeneratingCTS || isReadOnly}
          onClick={handleGenerateCTS}
        >
          Generate
        </Button>
      )}
    </>
  );
};

export default GeneratePanel;
